import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { stats } from "@/mocks/models";

function AnimatedNumber({ target }: { target: string }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef<HTMLSpanElement>(null);
  const hasRun = useRef(false);

  // Parse: "+500k" -> numeric=500, prefix="+", suffix="k"
  const parsed = target.match(/^([+\-]?)([\d,]+)\s*(.*)$/);
  const prefix = parsed?.[1] || "";
  const numericTarget = parsed ? parseInt(parsed[2].replace(/,/g, ""), 10) : 0;
  const suffix = parsed?.[3] || "";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasRun.current) {
          hasRun.current = true;
          const duration = 1500;
          const start = performance.now();

          const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * numericTarget);
            setDisplay(prefix + current.toLocaleString() + suffix);
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setDisplay(target);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [numericTarget, prefix, suffix, target]);

  return (
    <span ref={ref}>{display}</span>
  );
}

export default function StatsSection() {
  return (
    <motion.section
      className="px-4 py-5"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
    >
      <div className="flex items-center justify-between rounded-2xl bg-background-100/80 p-4 backdrop-blur-sm">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            className="flex flex-col items-center text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.12, duration: 0.4 }}
          >
            <div className="text-xl font-bold text-primary-400 md:text-2xl">
              <AnimatedNumber target={stat.num} />
            </div>
            <div className="mt-1 text-xs font-medium text-foreground-500">
              {stat.label}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}